import { Education } from '@applicator/shared'
import React from 'react'

import {
  EDUCATION_DATE_RANGE_ERROR_TITLE,
  isEducationDateRangeInvalid,
} from '../../utils/educationDateValidation'

import { getSaveHint, showDegreeConnectorWord } from './utils'
import { ValidationTooltip } from './ValidationTooltip'

interface EducationItemProps {
  education: Education
  editingInstitutionId: string | null
  editingLocationId: string | null
  editingDegreeId: string | null
  editingFieldOfStudyId: string | null
  editingAdditionalInfoId: string | null
  editingInstitution: string
  editingLocation: string
  editingDegree: string
  editingFieldOfStudy: string
  editingAdditionalInfo: string
  hoveredEducationId: string | null
  additionalInfoTextareaRef: React.RefObject<HTMLTextAreaElement>
  dateFieldRefs: React.MutableRefObject<{ [key: string]: HTMLElement | null }>
  onStartEditInstitution: (education: Education) => void
  onSaveInstitution: (educationId: string) => void
  onStartEditLocation: (education: Education) => void
  onSaveLocation: (educationId: string) => void
  onStartEditDegree: (education: Education) => void
  onSaveDegree: (educationId: string) => void
  onStartEditFieldOfStudy: (education: Education) => void
  onSaveFieldOfStudy: (educationId: string) => void
  onStartEditAdditionalInfo: (education: Education) => void
  onSaveAdditionalInfo: (educationId: string) => void
  onDeleteEducation: (educationId: string) => void
  onToggleEducation: (educationId: string) => void
  onToggleLocation: (educationId: string) => void
  onToggleDegree: (educationId: string) => void
  onToggleStartDate: (educationId: string) => void
  onToggleEndDate: (educationId: string) => void
  onToggleAdditionalInformation: (educationId: string) => void
  onStartEditStartDate: (education: Education, event: React.MouseEvent) => void
  onStartEditEndDate: (education: Education, event: React.MouseEvent) => void
  setHoveredEducationId: (id: string | null) => void
  setEditingInstitution: (value: string) => void
  setEditingLocation: (value: string) => void
  setEditingDegree: (value: string) => void
  setEditingFieldOfStudy: (value: string) => void
  setEditingAdditionalInfo: (value: string) => void
  setEditingInstitutionId: (id: string | null) => void
  setEditingLocationId: (id: string | null) => void
  setEditingDegreeId: (id: string | null) => void
  setEditingFieldOfStudyId: (id: string | null) => void
  setEditingAdditionalInfoId: (id: string | null) => void
  adjustTextareaHeight: (textarea: HTMLTextAreaElement) => void
  handleAdditionalInfoChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
  renderCheckbox: (checked: boolean, onChange: () => void) => React.ReactNode
}

export default function EducationItem({
  education,
  editingInstitutionId,
  editingLocationId,
  editingDegreeId,
  editingFieldOfStudyId,
  editingAdditionalInfoId,
  editingInstitution,
  editingLocation,
  editingDegree,
  editingFieldOfStudy,
  editingAdditionalInfo,
  hoveredEducationId,
  additionalInfoTextareaRef,
  dateFieldRefs,
  onStartEditInstitution,
  onSaveInstitution,
  onStartEditLocation,
  onSaveLocation,
  onStartEditDegree,
  onSaveDegree,
  onStartEditFieldOfStudy,
  onSaveFieldOfStudy,
  onStartEditAdditionalInfo,
  onSaveAdditionalInfo,
  onDeleteEducation,
  onToggleEducation,
  onToggleLocation,
  onToggleDegree,
  onToggleStartDate,
  onToggleEndDate,
  onToggleAdditionalInformation,
  onStartEditStartDate,
  onStartEditEndDate,
  setHoveredEducationId,
  setEditingInstitution,
  setEditingLocation,
  setEditingDegree,
  setEditingFieldOfStudy,
  setEditingAdditionalInfo,
  setEditingInstitutionId,
  setEditingLocationId,
  setEditingDegreeId,
  setEditingFieldOfStudyId,
  setEditingAdditionalInfoId,
  adjustTextareaHeight,
  handleAdditionalInfoChange,
  renderCheckbox,
}: EducationItemProps) {
  const dateRangeInvalid =
    education.included !== false &&
    isEducationDateRangeInvalid(education.startDate, education.endDate)
  const invalidDateFieldClass = dateRangeInvalid
    ? 'text-danger ring-1 ring-inset ring-danger/50'
    : 'text-text-secondary'

  return (
    <div 
      key={education.id}
      data-education-id={education.id}
      className="group space-y-2 relative"
    >
      {/* Institution */}
      <div 
        className="flex items-center gap-3"
        onMouseEnter={() => setHoveredEducationId(education.id)}
        onMouseLeave={() => setHoveredEducationId(null)}
      >
        <div className="flex items-center gap-3 flex-1 rounded transition-colors">
          {renderCheckbox(
            education.included ?? false,
            () => onToggleEducation(education.id)
          )}
          <div className="flex-1">
            {editingInstitutionId === education.id ? (
              <input
                type="text"
                value={editingInstitution}
                onChange={(e) => setEditingInstitution(e.target.value)}
                onBlur={() => onSaveInstitution(education.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    onSaveInstitution(education.id)
                  } else if (e.key === 'Escape') {
                    e.preventDefault()
                    setEditingInstitutionId(null)
                    setEditingInstitution('')
                  }
                }}
                placeholder="School or Institution"
                className="w-full px-2 py-1 text-sm border border-interactive rounded focus:outline-none focus:ring-2 focus:ring-interactive-focus-ring"
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
              />
            ) : (
              <div
                role="button"
                tabIndex={0}
                onClick={() => onStartEditInstitution(education)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onStartEditInstitution(education)
                  }
                }}
                className="cursor-text rounded p-1 -m-1"
              >
                {education.institution ? (
                  <div className="text-sm font-medium text-text-primary">{education.institution}</div>
                ) : (
                  <div className="text-sm text-text-muted italic">School or Institution</div>
                )}
              </div>
            )}
          </div>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation()
            onDeleteEducation(education.id)
          }}
          className={`p-1.5 text-text-muted hover:text-danger transition-opacity ${
            hoveredEducationId === education.id ? 'opacity-100' : 'opacity-0'
          }`}
          title="Delete education"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>

      {/* Location */}
      <div className="flex items-center gap-3 ml-6">
        {renderCheckbox(
          education.locationIncluded ?? false,
          () => onToggleLocation(education.id)
        )}
        <div className="flex-1">
          {editingLocationId === education.id ? (
            <input
              type="text"
              value={editingLocation}
              onChange={(e) => setEditingLocation(e.target.value)}
              onBlur={() => onSaveLocation(education.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  onSaveLocation(education.id)
                } else if (e.key === 'Escape') {
                  e.preventDefault()
                  setEditingLocationId(null)
                  setEditingLocation('')
                }
              }}
              placeholder="Location"
              className="px-2 py-1 text-sm border border-interactive rounded focus:outline-none focus:ring-2 focus:ring-interactive-focus-ring"
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
            />
          ) : (
            <div
              role="button"
              tabIndex={0}
              onClick={() => onStartEditLocation(education)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onStartEditLocation(education)
                }
              }}
              className="cursor-text rounded p-1 -m-1"
            >
              <div className="text-xs text-text-secondary">{education.location || 'Location'}</div>
            </div>
          )}
        </div>
      </div>

      {/* Degree and Field of Study on one line */}
      <div className="flex items-center gap-3 ml-6">
        {renderCheckbox(
          education.degreeIncluded ?? false,
          () => onToggleDegree(education.id)
        )}
        <div className="flex items-center gap-2 flex-wrap">
          {editingDegreeId === education.id ? (
            <input
              type="text"
              value={editingDegree}
              onChange={(e) => setEditingDegree(e.target.value)}
              onBlur={() => onSaveDegree(education.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  onSaveDegree(education.id)
                } else if (e.key === 'Escape') {
                  e.preventDefault()
                  setEditingDegreeId(null)
                  setEditingDegree('')
                }
              }}
              placeholder="Degree"
              className="px-2 py-1 text-sm border border-interactive rounded focus:outline-none focus:ring-2 focus:ring-interactive-focus-ring"
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
            />
          ) : (
            <div
              role="button"
              tabIndex={0}
              onClick={() => onStartEditDegree(education)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onStartEditDegree(education)
                }
              }}
              className="cursor-text rounded p-1 -m-1"
            >
              {education.degree ? (
                <span className="text-sm text-text-secondary">{education.degree}</span>
              ) : (
                <span className="text-sm text-text-muted italic">Degree</span>
              )}
            </div>
          )}
          {education.degree && (
            <>
              {showDegreeConnectorWord(education.degree) && (
                <span className="text-sm text-text-muted">degree </span>
              )}
              <span className="text-sm text-text-primary">in</span>
            </>
          )}
          {editingFieldOfStudyId === education.id ? (
            <input
              type="text"
              value={editingFieldOfStudy}
              onChange={(e) => setEditingFieldOfStudy(e.target.value)}
              onBlur={() => onSaveFieldOfStudy(education.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  onSaveFieldOfStudy(education.id)
                } else if (e.key === 'Escape') {
                  e.preventDefault()
                  setEditingFieldOfStudyId(null)
                  setEditingFieldOfStudy('')
                }
              }}
              placeholder="Field of Study"
              className="px-2 py-1 text-sm border border-interactive rounded focus:outline-none focus:ring-2 focus:ring-interactive-focus-ring"
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
            />
          ) : (
            <div
              role="button"
              tabIndex={0}
              onClick={() => onStartEditFieldOfStudy(education)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onStartEditFieldOfStudy(education)
                }
              }}
              className="cursor-text rounded p-1 -m-1"
            >
              {education.fieldOfStudy ? (
                <span className="text-sm text-text-secondary">{education.fieldOfStudy}</span>
              ) : (
                <span className="text-sm text-text-muted italic">Field of Study</span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Start Date and End Date on one line */}
      <ValidationTooltip
        active={dateRangeInvalid}
        message={EDUCATION_DATE_RANGE_ERROR_TITLE}
      >
        <div
          className="flex items-center gap-3 ml-6"
          data-education-date-range-invalid={dateRangeInvalid ? 'true' : undefined}
          aria-invalid={dateRangeInvalid || undefined}
        >
        <div className="flex items-center gap-2">
          {renderCheckbox(
            education.startDateIncluded ?? false,
            () => onToggleStartDate(education.id)
          )}
          <div
            ref={(el) => {
              const key = `${education.id}-startDate`
              dateFieldRefs.current[key] = el
            }}
            role="button"
            tabIndex={0}
            onClick={(e) => onStartEditStartDate(education, e)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onStartEditStartDate(education, e as unknown as React.MouseEvent)
              }
            }}
            className={`cursor-pointer hover:bg-surface-muted rounded px-1 -mx-1 text-xs ${invalidDateFieldClass}`}
          >
            {education.startDate || <span className="text-text-muted italic">Start Date</span>}
          </div>
        </div>
        {education.startDate && education.endDate && (
          <span className={`text-xs ${dateRangeInvalid ? 'text-danger' : 'text-text-muted'}`}>-</span>
        )}
        <div className="flex items-center gap-2">
          {renderCheckbox(
            education.endDateIncluded ?? false,
            () => onToggleEndDate(education.id)
          )}
          <div
            ref={(el) => {
              const key = `${education.id}-endDate`
              dateFieldRefs.current[key] = el
            }}
            role="button"
            tabIndex={0}
            onClick={(e) => onStartEditEndDate(education, e)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onStartEditEndDate(education, e as unknown as React.MouseEvent)
              }
            }}
            className={`cursor-pointer hover:bg-surface-muted rounded px-1 -mx-1 text-xs ${invalidDateFieldClass}`}
          >
            {education.endDate || <span className="text-text-muted italic">End Date</span>}
          </div>
        </div>
        </div>
      </ValidationTooltip>

      {/* Additional Information */}
      <div className="flex items-start gap-3 ml-6">
        {renderCheckbox(
          education.additionalInformationIncluded ?? false,
          () => onToggleAdditionalInformation(education.id)
        )}
        <div className="flex-1 relative">
          {editingAdditionalInfoId === education.id ? (
            <>
              <textarea
                ref={(el) => {
                  additionalInfoTextareaRef.current = el
                  if (el) {
                    setTimeout(() => adjustTextareaHeight(el), 0)
                  }
                }}
                value={editingAdditionalInfo}
                onChange={handleAdditionalInfoChange}
                onBlur={() => onSaveAdditionalInfo(education.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault()
                    onSaveAdditionalInfo(education.id)
                  } else if (e.key === 'Escape') {
                    e.preventDefault()
                    setEditingAdditionalInfoId(null)
                    setEditingAdditionalInfo('')
                  }
                }}
                onFocus={(e) => adjustTextareaHeight(e.target)}
                placeholder="Additional Information"
                rows={1}
                className="w-full px-2 py-1 pb-5 text-sm border border-interactive rounded focus:outline-none focus:ring-2 focus:ring-interactive-focus-ring resize-none overflow-hidden leading-relaxed"
                style={{ minHeight: '2.5rem' }}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
              />
              <div className="absolute bottom-1 right-2 text-xs text-text-muted pointer-events-none">
                {getSaveHint()}
              </div>
            </>
          ) : (
            <div
              role="button"
              tabIndex={0}
              onClick={() => onStartEditAdditionalInfo(education)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onStartEditAdditionalInfo(education)
                }
              }}
              className="cursor-text rounded p-1 -m-1"
            >
              {education.additionalInformation ? (
                <div className="text-xs text-text-secondary leading-relaxed whitespace-pre-wrap">{education.additionalInformation}</div>
              ) : (
                <div className="text-xs text-text-muted italic">Additional Information</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
